import styled, { DefaultTheme, createGlobalStyle } from 'styled-components';
import { Card, Button, Input, Select, Typography, Space } from 'antd';

declare module 'styled-components' {
  export interface DefaultTheme {
    mode?: 'light' | 'dark';
  }
}

const getThemeValue = <T,>(props: { theme?: DefaultTheme }, darkValue: T, lightValue: T): T => {
  return props.theme?.mode === 'dark' ? darkValue : lightValue;
};

export const Container = styled.div`
  width: 100%;
  padding: 16px;
  background: ${(props) => getThemeValue(props, 'transparent', '#f5f5f5')};

  @media (max-width: 768px) {
    padding: 16px;
  }
`;

export const StoreSwitcherRow = styled.div`
  width: 100%;
  margin-bottom: 16px;
`;

export const HeaderSection = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
  flex-wrap: wrap;
  gap: 16px;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
  }
`;

export const Title = styled(Typography.Title)`
  margin: 0 !important;
  color: ${(props) => getThemeValue(props, '#fff', '#1a1a1a')} !important;
  font-weight: 700 !important;
`;

export const Subtitle = styled(Typography.Text)`
  font-size: 16px;
  color: ${(props) => getThemeValue(props, '#8b949e', '#666')} !important;
  display: block;
  margin-top: 8px;
`;

export const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 16px;
  margin-bottom: 24px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

export const StatCard = styled(Card)`
  border-radius: 12px;
  border: 1px solid ${(props) => getThemeValue(props, '#30363d', '#e8e8e8')};
  background: ${(props) => getThemeValue(props, '#161b22', '#ffffff')};
  box-shadow: ${(props) =>
    getThemeValue(props, '0 2px 8px rgba(0, 0, 0, 0.3)', '0 2px 8px rgba(0, 0, 0, 0.08)')};
  transition: transform 0.2s, box-shadow 0.2s;

  &:hover {
    box-shadow: ${(props) =>
      getThemeValue(props, '0 4px 12px rgba(0, 0, 0, 0.4)', '0 4px 12px rgba(0, 0, 0, 0.12)')};
  }

  .ant-card-body {
    padding: 24px;
  }

  .ant-statistic-title {
    color: ${(props) => getThemeValue(props, '#8b949e', '#666')} !important;
  }

  .ant-statistic-content {
    color: ${(props) => getThemeValue(props, '#fff', '#1a1a1a')} !important;
  }

  .stat-help {
    font-size: 12px;
    color: ${(props) => getThemeValue(props, '#8b949e', '#666')};
    display: block;
    margin-top: 4px;
  }
`;

export const FiltersCard = styled(Card)`
  border-radius: 12px;
  border: 1px solid ${(props) => getThemeValue(props, '#30363d', '#e8e8e8')};
  background: ${(props) => getThemeValue(props, '#161b22', '#ffffff')};
  margin-bottom: 24px;

  .ant-card-body {
    padding: 16px 24px;
  }
`;

export const StyledInput = styled(Input)`
  max-width: 300px;
  border-radius: 8px;
  border: 1px solid ${(props) => getThemeValue(props, '#30363d', '#d9d9d9')};
  background: ${(props) => getThemeValue(props, '#0d1117', '#ffffff')};
  color: ${(props) => getThemeValue(props, '#fff', '#1a1a1a')};

  .search-prefix-icon {
    color: ${(props) => getThemeValue(props, '#8b949e', '#8b949e')};
    margin-right: 8px;
  }

  &:hover {
    border-color: #40a9ff;
  }

  &:focus,
  &.ant-input-focused {
    border-color: #1890ff;
    box-shadow: 0 0 0 2px rgba(24, 144, 255, 0.2);
  }
`;

export const StyledSelect = styled(Select)`
  min-width: 140px;
  max-width: 220px;
  width: 100%;
  border-radius: 8px;

  .ant-select-selector {
    border-radius: 8px !important;
    border: 1px solid ${(props) => getThemeValue(props, '#30363d', '#d9d9d9')} !important;
    background: ${(props) => getThemeValue(props, '#0d1117', '#ffffff')} !important;
    color: ${(props) => getThemeValue(props, '#fff', '#1a1a1a')} !important;
  }

  .ant-select-selection-item {
    overflow: visible !important;
    text-overflow: unset !important;
    white-space: normal !important;
  }
`;

export const OrdersStatusDropdownStyle = createGlobalStyle`
  .orders-status-select-dropdown {
    min-width: 220px !important;
  }
  .orders-status-select-dropdown .ant-select-item-option-content {
    display: block !important;
    overflow: visible !important;
    white-space: nowrap !important;
  }
`;

export const TabsCard = styled(Card)`
  border-radius: 12px;
  border: 1px solid ${(props) => getThemeValue(props, '#30363d', '#e8e8e8')};
  background: ${(props) => getThemeValue(props, '#161b22', '#ffffff')};
  overflow: hidden;

  .ant-card-body {
    padding: 0;
  }

  .ant-tabs {
    color: ${(props) => getThemeValue(props, '#fff', '#1a1a1a')};
  }

  .ant-tabs-nav {
    margin-bottom: 0;
    padding: 0 16px;
    background: ${(props) => getThemeValue(props, '#21262d', '#fafafa')};
  }

  .ant-tabs-tab {
    color: ${(props) => getThemeValue(props, '#8b949e', '#666')} !important;
  }

  .ant-tabs-tab-active .ant-tabs-tab-btn {
    color: ${(props) => getThemeValue(props, '#58a6ff', '#1890ff')} !important;
  }

  .ant-tabs-ink-bar {
    background: #1890ff;
  }

  .ant-tabs-content-holder {
    padding: 24px;
  }

  .orders-tab-label {
    display: inline-flex;
    align-items: center;
  }

  .tab-badge {
    margin-left: 4px;
    padding: 2px 8px;
    font-size: 12px;
    border-radius: 10px;
    background: ${(props) => getThemeValue(props, '#30363d', '#e8e8e8')};
    color: ${(props) => getThemeValue(props, '#fff', '#1a1a1a')};
    font-weight: 500;
  }

  .ant-tabs-tab-active .tab-badge {
    background: ${(props) => getThemeValue(props, '#388bfd26', '#e6f4ff')};
    color: ${(props) => getThemeValue(props, '#58a6ff', '#1890ff')};
  }
`;

export const OrderCard = styled(Card)`
  border-radius: 14px;
  border: 1px solid ${(props) => getThemeValue(props, '#30363d', '#e8e8e8')};
  background: ${(props) => getThemeValue(props, '#161b22', '#ffffff')};
  box-shadow: ${(props) =>
    getThemeValue(props, '0 1px 3px rgba(0, 0, 0, 0.2)', '0 1px 3px rgba(0, 0, 0, 0.06)')};
  transition: box-shadow 0.2s ease, border-color 0.2s ease;
  margin-bottom: 16px;
  overflow: hidden;

  &:hover {
    box-shadow: ${(props) =>
      getThemeValue(props, '0 4px 12px rgba(0, 0, 0, 0.25)', '0 2px 8px rgba(0, 0, 0, 0.08)')};
    border-color: ${(props) => getThemeValue(props, '#404854', '#d9d9d9')};
  }

  .ant-card-body {
    padding: 20px 24px;
  }

  .ant-tag {
    display: inline-flex;
    align-items: center;
    gap: 8px;
  }

  .ant-tag-icon {
    margin-right: 0;
  }
`;

export const OrderHeaderRow = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: flex-start;
  flex-wrap: wrap;
  gap: 24px;
  margin-bottom: 0;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
    gap: 20px;
  }
`;

export const OrderInfoLeft = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  min-width: 0;
  flex: 1 1 280px;
`;

export const OrderMeta = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 16px 24px;
  align-items: center;

  .order-meta-item {
    display: inline-flex;
    align-items: center;
    gap: 8px;
  }

  .order-meta-text {
    color: ${(props) => getThemeValue(props, '#8b949e', '#666')};
    font-size: 14px;
  }

  .order-meta-value {
    color: ${(props) => getThemeValue(props, '#fff', '#1a1a1a')};
    font-weight: 600;
    font-size: 14px;
  }
`;

export const OrderAmounts = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 6px;

  .amount-row {
    display: flex;
    align-items: baseline;
    justify-content: flex-end;
    width: 100%;
    max-width: 140px;
  }

  .amount-label {
    font-size: 12px;
    color: ${(props) => getThemeValue(props, '#8b949e', '#666')};
    flex: 0 0 52px;
    text-align: right;
  }

  .amount-value {
    font-weight: 600;
    font-size: 14px;
    color: ${(props) => getThemeValue(props, '#fff', '#1a1a1a')};
    min-width: 60px;
    text-align: right;
  }
`;

export const OrderActionsBlock = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 0;

  .order-amounts-wrap {
    margin-bottom: 16px;
  }
`;

export const StyledButton = styled(Button)`
  border-radius: 8px;
  font-weight: 500;
  min-width: 120px;
  margin-top: 4px;
`;

export const RefreshButton = styled(Button)`
  border-radius: 8px;
  border: 1px solid ${(props) => getThemeValue(props, '#30363d', '#d9d9d9')};
  background: ${(props) => getThemeValue(props, '#21262d', '#ffffff')};
  color: ${(props) => getThemeValue(props, '#fff', '#1a1a1a')};

  &:hover:not(:disabled) {
    border-color: #1890ff;
    color: #1890ff;
    background: ${(props) => getThemeValue(props, '#21262d', '#ffffff')};
  }
`;

export const ItemCard = styled(Card)`
  border-radius: 8px;
  border: 1px solid ${(props) => getThemeValue(props, '#30363d', '#e8e8e8')};
  background: ${(props) => getThemeValue(props, '#0d1117', '#fafafa')};
  margin-bottom: 12px;

  .ant-card-body {
    padding: 16px;
  }
`;

export const CustomizationRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding-left: 16px;
  border-left: 2px solid #1890ff;
  margin-top: 8px;
`;

export const OrderItemsWrap = styled(Space)`
  width: 100%;
  max-height: 400px;
  overflow-y: auto;
  padding-right: 8px;

  &.ant-space {
    width: 100%;
  }

  /* Scrollbar styling */
  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: ${(props) => getThemeValue(props, '#0d1117', '#f5f5f5')};
    border-radius: 3px;
  }

  &::-webkit-scrollbar-thumb {
    background: ${(props) => getThemeValue(props, '#30363d', '#d9d9d9')};
    border-radius: 3px;

    &:hover {
      background: ${(props) => getThemeValue(props, '#404854', '#bdbdbd')};
    }
  }
`;

export const ItemCardInner = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  width: 100%;
  gap: 12px;

  .item-name {
    font-size: 16px;
    font-weight: 600;
    color: ${(props) => getThemeValue(props, '#fff', '#1a1a1a')};
    flex: 1;
    word-break: break-word;
    display: flex;
    align-items: center;
    gap: 8px;

    svg {
      flex-shrink: 0;
      color: #1890ff;
    }
  }

  .item-details {
    display: flex;
    align-items: center;
    gap: 12px;
    white-space: nowrap;
    flex-shrink: 0;
  }
`;

export const CustomizationsWrap = styled.div`
  margin-top: 12px;

  .customization-label {
    font-weight: 500;
    margin-bottom: 8px;
  }
`;

export const EmptyCustomizationsWrap = styled.div`
  margin-top: 8px;
  padding: 8px 12px;
  border-left: 2px solid #1890ff;
  color: ${(props) => getThemeValue(props, '#8b949e', '#999')};
  font-size: 13px;
`;

export const PhoneBlurred = styled.span`
  filter: blur(3px);
`;

export const AmountValueTotal = styled.span`
  font-weight: 600;
  color: #52c41a !important;
`;

export const SectionTitle = styled(Typography.Title)`
  margin-bottom: 16px !important;
  color: ${(props) => getThemeValue(props, '#fff', '#1a1a1a')} !important;
  font-weight: 600 !important;
`;

export const EmptyState = styled.div`
  text-align: center;
  padding: 48px 24px;
  color: ${(props) => getThemeValue(props, '#8b949e', '#999')};
`;

export const EmptyStateIcon = styled.div`
  font-size: 48px;
  margin-bottom: 16px;
  opacity: 0.6;
`;

export const EmptyStateText = styled(Typography.Text)`
  font-size: 16px;
  color: ${(props) => getThemeValue(props, '#8b949e', '#999')} !important;
  display: block;
  margin-top: 8px;
`;

export const OrderCardSkeleton = styled(Card)`
  border-radius: 12px;
  border: 1px solid ${(props) => getThemeValue(props, '#30363d', '#e8e8e8')};
  background: ${(props) => getThemeValue(props, '#161b22', '#ffffff')};
  margin-bottom: 16px;

  .ant-card-body {
    padding: 20px 24px;
  }

  .ant-skeleton-content .ant-skeleton-title {
    margin-top: 0;
  }
`;

export const FiltersRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;
`;

export const TitleIcon = styled.span`
  margin-right: 8px;
`;

export const ExpandedRowWrap = styled.div`
  padding: 8px 24px 16px;
`;

export const ExpandedRowTitle = styled.div`
  font-weight: 600;
  margin-bottom: 12px;
  color: ${(props) => getThemeValue(props, '#fff', '#1a1a1a')};
`;

export const ExpandedItemBlock = styled.div`
  padding: 8px 12px;
  margin-bottom: 8px;
  background: ${(props) => getThemeValue(props, '#0d1117', '#fafafa')};
  border-radius: 8px;
  border: 1px solid ${(props) => getThemeValue(props, '#30363d', '#e8e8e8')};

  .expanded-item-row {
    width: 100%;
    justify-content: space-between;
  }
`;

export const ExpandedCustomizationWrap = styled.div`
  margin-top: 8px;
  padding-left: 16px;
  border-left: 2px solid #1890ff;

  div {
    margin-top: 4px;
  }
`;

export const TablePhoneBlurred = styled.span`
  filter: blur(3px);
`;
